// Core
import { useMemo, useState } from "react";
import PropTypes from "prop-types";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

// Components
import {
  Button,
  Chip,
  DataTable,
  FieldSearchSelect,
  FieldText,
  Modal,
  SectionCard,
} from "components/AtomsV2";
import { Icon } from "components/ShellV2";

// Redux
import { useDispatch, useSelector } from "react-redux";
import { openNotificationPopup, openWarningAlert } from "state/application/applicationSlice";
import { selectCurrentCommunity } from "state/communities/communitiesSlice";
import {
  useAddOwnershipByMemberIdResourceIdMutation,
  useDeleteOwnershipByMemberIdResourceIdMutation,
  useUpdateOwnershipByMemberIdResourceIdMutation,
} from "state/ownerships/ownershipsApiSlice";
import {
  selectAllResources,
  useGetResourcesByCommunityIdQuery,
} from "state/resources/resourcesApiSlice";

// Utils
import { resourceVisual } from "utils/resourceTypes";

function ListOwnerships({ data: ownerships }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const community = useSelector(selectCurrentCommunity);
  const { id: memberId } = useParams();

  const { t } = useTranslation(["members", "community"]);

  // GET resources
  const { resources } = useGetResourcesByCommunityIdQuery(
    { communityId: community.id, excludeDeleted: "true" },
    {
      selectFromResult: ({ data }) => ({ resources: (data && selectAllResources(data)) || [] }),
    }
  );

  // Modal
  const [open, setOpen] = useState(false);
  const [actionType, setActionType] = useState("CREATE");
  const [resourceId, setResourceId] = useState(resources.length > 0 ? resources[0].id : "");
  const [percOwnership, setPercOwnership] = useState(0);
  const handleClose = () => {
    setOpen(false);
    setActionType("CREATE");
    setResourceId(resources.length > 0 ? resources[0].id : "");
    setPercOwnership(0);
  };

  const resourcesList = resources.map((resource) => ({
    value: resource.id,
    label: resource.name,
  }));

  // CREATE ownership
  const onCreateOwnershipClicked = () => {
    setActionType("CREATE");
    setResourceId(resources.length > 0 ? resources[0].id : "");
    setPercOwnership(0);
    setOpen(true);
  };
  const [createOwnershipByMemberIdResourceId] = useAddOwnershipByMemberIdResourceIdMutation();
  const onCreateOwnership = async () => {
    try {
      await createOwnershipByMemberIdResourceId({
        communityId: community.id,
        memberId,
        resourceId,
        percOwnership,
      }).unwrap();
      return handleClose();
    } catch (err) {
      return dispatch(openNotificationPopup({ notification: err, type: "error" }));
    }
  };

  // UPDATE ownership
  const onUpdateOwnershipClicked = (resourceIdSelected) => {
    setActionType("UPDATE");
    setResourceId(resourceIdSelected);
    setPercOwnership(
      ownerships.find((ownership) => ownership.resource_id === resourceIdSelected)?.perc_ownership
    );
    setOpen(true);
  };
  const [updateOwnership] = useUpdateOwnershipByMemberIdResourceIdMutation();
  const onUpdatePercOwnership = async () => {
    try {
      await updateOwnership({
        communityId: community.id,
        memberId,
        resourceId,
        percOwnership,
      }).unwrap();
      return handleClose();
    } catch (err) {
      return dispatch(openNotificationPopup({ notification: err, type: "error" }));
    }
  };

  // DELETE ownership
  const [deleteOwnership] = useDeleteOwnershipByMemberIdResourceIdMutation();
  const onDeleteOwnership = async (resourceIdSelected) => {
    const action = async () => {
      try {
        await deleteOwnership({
          communityId: community.id,
          memberId,
          resourceId: resourceIdSelected,
        }).unwrap();
      } catch (err) {
        dispatch(
          openNotificationPopup({
            error: err,
            type: "error",
          })
        );
      }
    };

    dispatch(
      openWarningAlert({
        confirmText: t("delete.confirm_button", { ns: "community" }),
        confirmAction: action,
      })
    );
  };

  const rows = useMemo(
    () =>
      (ownerships || []).map((o) => {
        const resource = resources.find((r) => r.id === o.resource_id);
        return {
          id: o.resource_id,
          name: resource?.name || o.resource_id,
          type: resource?.type || "—",
          perc: o.perc_ownership,
        };
      }),
    [ownerships, resources]
  );

  const typeLabel = (type) =>
    t(`list.card.resource_types.${type}`, { ns: "community", defaultValue: type });

  const columns = [
    {
      key: "name",
      label: t("single_member.ownerships.table.columns.resource_name"),
      render: (v, row) => {
        const visual = resourceVisual(row.type);
        return (
          <div className="rec-cell-member">
            <div className={`rec-av-sm ${visual.tint}`}>
              <Icon name={visual.icon} style={{ fontSize: 18 }} />
            </div>
            <b>{v}</b>
          </div>
        );
      },
    },
    {
      key: "type",
      label: t("single_member.ownerships.table.columns.type"),
      render: (v) => <Chip tone="gray">{typeLabel(v)}</Chip>,
    },
    {
      key: "perc",
      label: t("single_member.ownerships.table.columns.perc_ownership"),
      align: "right",
      render: (v) => <Chip tone="teal">{`${v}%`}</Chip>,
    },
    {
      key: "_actions",
      label: "",
      sort: false,
      align: "right",
      render: (v, row) => (
        <div className="rec-row-actions">
          <button
            type="button"
            className="rec-icon-btn"
            title={t("table.actions.edit")}
            onClick={(e) => {
              e.stopPropagation();
              onUpdateOwnershipClicked(row.id);
            }}
          >
            <Icon name="edit" />
          </button>
          <button
            type="button"
            className="rec-icon-btn"
            title={t("table.actions.delete")}
            onClick={(e) => {
              e.stopPropagation();
              onDeleteOwnership(row.id);
            }}
          >
            <Icon name="delete" />
          </button>
        </div>
      ),
    },
  ];

  return (
    <SectionCard
      id="ownerships"
      icon="settings"
      title={t("single_member.ownerships.title")}
      action={
        <Button size="sm" onClick={onCreateOwnershipClicked}>
          <Icon name="add" />
          {t("single_member.ownerships.add_button")}
        </Button>
      }
    >
      <div className="-mx-[var(--pad-card)] -mb-[var(--pad-card)]">
        <DataTable
          columns={columns}
          rows={rows}
          search={false}
          onRow={(row) =>
            navigate(`/communities/${community.id}/infrastructure/resources/${row.id}`)
          }
        />
      </div>

      <Modal
        open={open}
        onClose={handleClose}
        title={t("single_member.ownerships.modal.title")}
        maxWidth={460}
        footer={
          <>
            <Button variant="ghost" onClick={handleClose}>
              {t("new.cancel", { ns: "community" })}
            </Button>
            <Button
              disabled={!resourceId || !percOwnership}
              onClick={actionType === "CREATE" ? onCreateOwnership : onUpdatePercOwnership}
            >
              {actionType === "CREATE"
                ? t("new.create", { ns: "community" })
                : t("new.update", { ns: "community" })}
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-[14px]">
          <FieldSearchSelect
            label={t("single_member.ownerships.fields.resource")}
            value={resourceId ?? ""}
            options={resourcesList}
            onChange={setResourceId}
            disabled={actionType === "UPDATE"}
          />
          <FieldText
            label={t("single_member.ownerships.fields.perc_ownership")}
            type="number"
            suffix="%"
            value={percOwnership}
            onChange={setPercOwnership}
          />
        </div>
      </Modal>
    </SectionCard>
  );
}

ListOwnerships.defaultProps = {
  data: [],
};

ListOwnerships.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      resource_id: PropTypes.string,
      perc_ownership: PropTypes.number,
    })
  ),
};

export default ListOwnerships;
